import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type { ComponentProps } from "react";

import { cn } from "@/lib/cn";

const buttonVariants = cva(
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold " +
    "transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // El hover sale de un TOKEN y no de un blanco literal: `ink` y `canvas` se
        // invierten dentro de un panel claro, así que aclarar hasta blanco dejaba texto
        // blanco sobre fondo blanco y el botón desaparecía bajo el ratón. El token sabe
        // hacia dónde aclarar en cada contexto; un literal solo sirve donde se escribió.
        // Hay un barrido en `button.test.tsx` que no deja volver a ponerlo.
        primary: "bg-ink text-canvas shadow-soft hover:bg-ink-hover",
        secondary: "glass text-ink hover:bg-white/10",
        ghost: "text-light-2 hover:bg-white/5 hover:text-light",
        danger: "bg-bad text-canvas hover:bg-bad/90",
      },
    },
    defaultVariants: { variant: "primary" },
  },
);

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    /** Usa el hijo como elemento, por ejemplo un enlace con aspecto de botón. */
    asChild?: boolean;
  };

export function Button({ className, variant, asChild = false, ...props }: ButtonProps) {
  const Component = asChild ? Slot.Root : "button";
  return <Component className={cn(buttonVariants({ variant }), className)} {...props} />;
}
